import type { CookieConsent } from './cookie-consent';

const STORAGE_KEY = 'ipodariger.cookie-consent';

/**
 * The visitor's cookie decision, held in one place the way the session is.
 *
 * **It lives outside React because analytics is not a component.** The banner needs to re-render
 * when the choice changes, but the thing that acts on the choice — loading a third-party script —
 * is a side effect on the document, and both have to read the same answer. A context alone would
 * make the loader a component's problem; this makes it the store's subscribers'.
 *
 * **A browser that refuses storage is treated as undecided, every visit.** That is the honest
 * failure: it asks again rather than silently assuming consent, and the only cost is a banner in a
 * private window. Consent that cannot be recorded has not been given.
 *
 * `localStorage` rather than a cookie of its own, deliberately. A consent record does not need to
 * reach the server — nothing on the server branches on it — and a cookie would be one more cookie
 * to explain on the page that explains the cookies.
 */
let current: CookieConsent | null = read();

const listeners = new Set<() => void>();

function read(): CookieConsent | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);

        // Anything else in the slot is somebody else's value or a half-written one; an
        // unrecognised string must never read as consent.
        return stored === 'granted' || stored === 'denied' ? stored : null;
    } catch {
        return null;
    }
}

function emit(): void {
    for (const listener of listeners) {
        listener();
    }
}

export function subscribeToConsent(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Stable between changes, so `useSyncExternalStore` does not loop. */
export function getConsent(): CookieConsent | null {
    return current;
}

export function setConsent(consent: CookieConsent): void {
    current = consent;

    try {
        localStorage.setItem(STORAGE_KEY, consent);
    } catch {
        // The choice still holds for this page. It will be asked again on the next visit, which is
        // the right way round to be wrong.
    }

    emit();
}

/**
 * Forgets the decision, so the banner asks again.
 *
 * This is what the "change your choice" control on the legal page does. Withdrawing consent is a
 * right the policy on that page states, so there has to be a way to exercise it that is not
 * "clear your browser's site data".
 */
export function clearConsent(): void {
    current = null;

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to do — the in-memory value is already gone.
    }

    emit();
}

/** For tests: re-reads storage into the module's state. Not used by the app. */
export function reloadConsentForTests(): void {
    current = read();
}

/** Undecided, which is what a build-time render has to assume — see `getServerSession`. */
export function getServerConsent(): CookieConsent | null {
    return null;
}
